import { useState, useEffect } from 'react';
import { Activity, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';
import { vppMonitoringService } from '../../../services';
import type { FlowprobeConfig } from '../../../services';
import { useTenantStore } from '../../../stores/tenantStore';

interface FlowExportSummaryProps {
    edgeId: string;
}

const FlowExportSummary = ({ edgeId }: FlowExportSummaryProps) => {
    const selectedTenantId = useTenantStore((s) => s.selectedTenantId);
    const [config, setConfig] = useState<FlowprobeConfig | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchConfig();
    }, [edgeId, selectedTenantId]);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            const response = await vppMonitoringService.getFlowprobeConfig(edgeId);
            setConfig(response.config || null);
        } catch (error) {
            console.error('Failed to fetch flowprobe config:', error);
            setConfig(null);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-32">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    if (!config) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-gray-900">Flow Export Not Configured</h3>
                <p className="text-sm text-gray-500 mt-1">
                    IPFIX flow export is not configured for this edge.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                    <Activity className="w-5 h-5 text-primary-600" />
                    <h3 className="text-lg font-semibold text-gray-900">Flow Export (IPFIX) Summary</h3>
                </div>
                <div className="flex items-center space-x-2">
                    {config.enabled ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Enabled
                        </span>
                    ) : (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            Disabled
                        </span>
                    )}
                </div>
            </div>

            {config.enabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Collector Details</h4>
                        <div className="space-y-3">
                            <div>
                                <span className="block text-xs text-gray-500">Address</span>
                                <span className="font-mono text-sm text-gray-900">{config.collector_address}</span>
                            </div>
                            <div>
                                <span className="block text-xs text-gray-500">Port</span>
                                <span className="font-mono text-sm text-gray-900">{config.collector_port}</span>
                            </div>
                            <div>
                                <span className="block text-xs text-gray-500">Path MTU</span>
                                <span className="font-mono text-sm text-gray-900">{config.path_mtu}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white border rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-500 mb-3 uppercase tracking-wider">Configuration</h4>
                        <div className="space-y-3">
                            <div>
                                <span className="block text-xs text-gray-500">Interfaces</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {(!config.interfaces || config.interfaces.length === 0) ? (
                                        <span className="text-sm text-gray-600">Auto-detect</span>
                                    ) : (
                                        config.interfaces.map(iface => (
                                            <span key={iface} className="inline-flex px-2 py-0.5 rounded bg-gray-100 text-xs font-mono text-gray-800 border-gray-200 border">
                                                {iface}
                                            </span>
                                        ))
                                    )}
                                </div>
                            </div>
                            <div>
                                <span className="block text-xs text-gray-500">Layers Recorded</span>
                                <div className="flex gap-2 mt-1">
                                    {['l2', 'l3', 'l4'].map(layer => (
                                        <span key={layer} className={`text-xs px-2 py-0.5 rounded ${config[`record_${layer}` as keyof FlowprobeConfig] ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-400'}`}>
                                            {layer.toUpperCase()}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <span className="block text-xs text-gray-500">Timers</span>
                                <span className="text-sm text-gray-900">
                                    Template: {config.template_interval}s | Active: {config.active_timer}s | Passive: {config.passive_timer}s
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FlowExportSummary;
